import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Leaf, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SiteNav } from '@/components/SiteNav';
import { SiteFooter } from '@/components/SiteFooter';

const values = [
  {
    icon: Target,
    title: 'Precision Engineered',
    description: 'Every product we carry has been field-tested in real conditions — not just a lab. If it doesn\'t perform, it doesn\'t make the cut.',
  },
  {
    icon: Leaf,
    title: 'Conservation First',
    description: 'We support ethical hunting and wildlife conservation. A portion of every sale goes toward habitat preservation across the Dakotas.',
  },
  {
    icon: Users,
    title: 'Built for Community',
    description: 'We\'re more than a gear brand. We\'re a community of hunters, anglers, and outdoor enthusiasts sharing stories from the field.',
  },
];

export function AboutPage() {
  useSeoMeta({
    title: 'About Us — Dakota Horizon Outdoors',
    description: 'Born on the prairie, built for the hunt. Learn the story behind Dakota Horizon Outdoors.',
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(10,20,5,0.92) 0%, rgba(30,20,10,0.80) 100%), 
              url('https://images.unsplash.com/photo-1501854140801-50d01698950b?w=1400&q=80')`,
          }}
        />
        <div className="relative container mx-auto px-4 text-center">
          <Badge className="bg-amber-600/30 text-amber-400 border-amber-600/50 mb-4">Our Story</Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Born on the Prairie.
            <span className="block text-amber-500">Built for the Hunt.</span>
          </h1>
          <p className="text-stone-300 text-xl max-w-2xl mx-auto leading-relaxed">
            Dakota Horizon Outdoors was founded by hunters, for hunters. Our mission is simple: deliver the best gear for the people who live for the outdoors.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-foreground mb-6">The Dakota Horizon Story</h2>
              <div className="space-y-4 text-muted-foreground text-base leading-relaxed">
                <p>
                  It started the way most great things do — with cold mornings, muddy boots, and a deep respect for the land. Our founders spent decades hunting whitetail deer in the Missouri River bottoms and chasing walleye across the glacial lakes of the Dakotas.
                </p>
                <p>
                  After years of frustration with gear that looked great in catalogs but failed in the field, they decided to do something about it. Dakota Horizon Outdoors was born with one goal: build and source gear that actually works when the temperature drops and the stakes are high.
                </p>
                <p>
                  Today, we're proud to serve more than 50,000 hunters, anglers, and outdoor enthusiasts across the country. Every product in our lineup has been tested by real sportsmen in real conditions — nothing makes it to the shelf unless we'd trust it with our own hunts.
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-600/20 rounded-full blur-3xl" />
                <img
                  src="/images/DHO.jpg"
                  alt="Dakota Horizon Outdoors Logo"
                  className="relative h-72 w-72 rounded-full object-cover ring-4 ring-amber-700/50 shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted/40">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-amber-700/20 text-amber-700 border-amber-700/30 mb-3">What We Stand For</Badge>
            <h2 className="text-4xl font-bold text-foreground">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map(({ icon: Icon, title, description }) => (
              <div key={title} className="text-center p-8 rounded-lg bg-card border border-border/50 product-card-hover">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-amber-700/10 border border-amber-700/30 mb-4">
                  <Icon className="h-6 w-6 text-amber-700" />
                </div>
                <h3 className="font-bold text-xl text-foreground mb-3">{title}</h3>
                <p className="text-muted-foreground leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[hsl(85,35%,18%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Gear Up?</h2>
          <p className="text-stone-300 text-lg mb-8 max-w-md mx-auto">
            Browse our full catalog of premium hunting, fishing, and outdoor gear.
          </p>
          <Button asChild className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-8 py-6 text-base rounded-sm">
            <Link to="/hunting">
              Shop All Gear <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

export default AboutPage;
